import { Outlet, useNavigate } from 'react-router-dom';
import Loading from '../ions/Loading';
import PageHeader from '../molecules/PageHeader';
import useFetch, { FetchHeader } from '../../hooks/useFetch';
import ATable, { ATableHeader } from '../organisms/ATable';
import { ReactNode, useRef, useEffect } from 'react';
import PageFooter from '../molecules/PageFooter';

const ATABLE_MARGIN_TOP_DEFAULT = 176;
interface BreadcrumbItem {
    label: string;
    url: string;
}

type PageListProps = {
    pageTitle: string;
    url: string;
    fetchHeaders?: FetchHeader[];
    breadcrumb: BreadcrumbItem[];
    children: {
        controlSlot: ReactNode;
        filterSlot: ReactNode;
        UIComponents?: ReactNode;
    };
    tableHeaders?: ATableHeader[];
    tableFieldSort: string;
    tableOrderSort: 'ASC' | 'DESC';
    onTableSort: (field: string, order: 'ASC' | 'DESC') => void;
    onTableSelect?: (checked: boolean, rows: never[]) => void;
    renderRows: (rows: never[]) => ReactNode;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onItemsPerPageChange: (pageSize: number) => void;
    itemsLabel: string;
    defaultBackendHeader?: boolean;
    isLoadingData?: boolean;
    filterRows?: (row: never) => void;
    aTableMarginTopDefault?: number;
    onGotApiPayload?: (data: any) => void;
    /**
     * Avisa quando a busca começa e termina.
     *
     * ⚠️ Não dá para inferir isso do `onGotApiPayload`: ele é chamado DURANTE o render, a cada
     * render, e enquanto a busca nova está em voo o `data` ainda é o anterior — quem tentasse usá-lo
     * como "terminou" receberia o aviso no mesmo instante em que pediu a busca. Foi assim que o
     * botão de atualizar nunca chegou a girar.
     */
    onLoadingChange?: (loading: boolean) => void;
    externalTotalElements?: number;
    externalTotalPages?: number;
};

const PageList = ({
    pageTitle,
    url,
    fetchHeaders,
    breadcrumb,
    children,
    tableHeaders,
    tableFieldSort,
    tableOrderSort,
    onTableSort,
    renderRows,
    page,
    pageSize,
    onPageChange,
    onItemsPerPageChange,
    defaultBackendHeader = true,
    itemsLabel,
    onTableSelect,
    isLoadingData = false,
    filterRows = () => true,
    aTableMarginTopDefault = ATABLE_MARGIN_TOP_DEFAULT,
    onGotApiPayload = () => { },
    onLoadingChange,
    externalTotalElements,
    externalTotalPages,
}: PageListProps) => {
    const navigate = useNavigate();

    const rows = useRef([]);
    const columns = useRef<ATableHeader[]>([]);
    const size = useRef(0);
    const apiVersion = useRef('');
    const totalPages = useRef(0);
    const totalElements = useRef(0);
    const numberOfElements = useRef(0);

    const selectAllRows = !!onTableSelect;

    const { controlSlot, filterSlot, UIComponents } = children;

    const { isLoading, data, error, isTokenError } = useFetch(url, fetchHeaders);

    /* Os callbacks do pai ficam num ref: o pai costuma recriá-los a cada render, e um efeito que
       dependesse da identidade deles rodaria de novo a cada render — com `onGotApiPayload` gravando
       um estado novo no pai, isso vira laço (review da PR #1208). Os efeitos rodam quando o DADO
       muda, e chamam sempre a versão mais recente do callback. */
    const onGotApiPayloadRef = useRef(onGotApiPayload);
    onGotApiPayloadRef.current = onGotApiPayload;
    const onLoadingChangeRef = useRef(onLoadingChange);
    onLoadingChangeRef.current = onLoadingChange;

    useEffect(() => { onLoadingChangeRef.current?.(isLoading); }, [isLoading]);

    useEffect(() => {
        if (isTokenError) {
            navigate('/denied');
        } else if (error) {
            navigate('/network-error');
        }
    }, [isTokenError, error, navigate]);

    /**
     * Avisa o pai sobre o payload recém-chegado.
     *
     * ⚠️ Isto tem que ser um efeito, não uma chamada dentro do render: `onGotApiPayload` costuma
     * chamar um `setState` de um componente ANCESTRAL (ex.: `PaymentsPage` guardando `rows`), e
     * atualizar um componente diferente NO MEIO do render deste aqui é exatamente o padrão que o
     * React proíbe — ele corrompe a contabilidade de hooks e derruba a árvore inteira assim que o
     * primeiro payload de verdade chega (antes disso `data` era sempre `null` e o bug ficava
     * escondido).
     */
    useEffect(() => {
        if (data) onGotApiPayloadRef.current(data);
    }, [data]);

    if (!defaultBackendHeader && tableHeaders) columns.current = tableHeaders;

    if (data) {
        size.current = data.size;
        totalPages.current = data.totalPages;
        totalElements.current = data.totalElements;
        numberOfElements.current = data.numberOfElements;

        apiVersion.current = `${data.apiName} - ${data.apiVersion}`;

        if (defaultBackendHeader) columns.current = data.headers;

        rows.current = data.list || [];

        if (rows.current instanceof Array) {
            rows.current = rows.current.filter((row: never) => filterRows(row));
        } else {
            rows.current = [];
        }
    }

    const onSelect = (checked: boolean) => {
        if (onTableSelect) onTableSelect(checked, rows.current);
    };

    return (
        <div className="px-4 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100">
            <div className="grid grid-cols-1">
                {(isLoadingData || isLoading) && <Loading />}
                <PageHeader title={pageTitle} apiVersion={apiVersion.current} breadcrumb={breadcrumb}>
                    <div className="pt-4">{controlSlot}</div>
                </PageHeader>

                {filterSlot}


                <ATable
                    headers={columns.current}
                    fieldSorted={tableFieldSort}
                    orderSorted={tableOrderSort}
                    onSort={onTableSort}
                    onSelect={onSelect}
                    autoResize={true}
                    canSelectAll={selectAllRows}
                    marginTop={aTableMarginTopDefault}
                >
                    {Array.isArray(rows.current) ? renderRows(rows.current) : null}
                </ATable>

                <PageFooter
                    currentPage={page}
                    totalPages={externalTotalPages ?? totalPages.current}
                    itemsPerPage={pageSize}
                    totalElements={externalTotalElements ?? totalElements.current}
                    onPageChange={onPageChange}
                    onItemsPerPageChange={onItemsPerPageChange}
                    itemsLabel={itemsLabel}
                />
                <Outlet />
                {UIComponents}
            </div>
        </div>
    );
};

export default PageList;
